class LookCartModal extends HTMLElement {
  constructor() {
    super();
    this.products = [];
    this.selectedVariants = {};
    this.dialog = null;
  }

  connectedCallback() {
    try {
      this.products = JSON.parse(this.dataset.products);
    } catch {
      this.products = [];
    }
    this.preselectFirstAvailableVariants();
    this.addText = this.dataset.addText || 'Add to cart';
    this.selectText = this.dataset.selectText || 'Select sizes';
    this.closeText = this.dataset.closeText || 'Close';
    this.soldOutText = this.dataset.soldOutText || 'Sold out';
    this.sizeRequiredText = this.dataset.sizeRequiredText || 'Please select a size for all products';
    this.addedText = this.dataset.addedText || 'Added to cart';

    const trigger = this.querySelector('button');
    if (trigger) {
      trigger.addEventListener('click', () => this.open());
    }
  }

  open() {
    if (this.products.length === 0) {
      return;
    }

    if (this.dialog) {
      this.dialog.showModal();
      return;
    }
    this.buildModal();
    this.dialog.showModal();
  }

  close() {
    if (this.dialog) {
      this.dialog.close();
    }
  }

  preselectFirstAvailableVariants() {
    this.products.forEach((product) => {
      if (this.selectedVariants[product.id] !== undefined) {
        return;
      }

      const firstAvailableVariant = product.variants.find((variant) => variant.available);
      if (firstAvailableVariant) {
        this.selectedVariants[product.id] = firstAvailableVariant.id;
      }
    });
  }

  getSelectableProducts() {
    return this.products.filter((product) => {
      return product.variants.some((variant) => variant.available);
    });
  }

  buildModal() {
    this.dialog = document.createElement('dialog');
    this.dialog.classList.add('look-modal');
    this.dialog.setAttribute('aria-label', this.selectText);

    this.dialog.innerHTML = `
      <div class="look-modal__content">
        <div class="look-modal__header">
          <h3 class="look-modal__title">${this.selectText}</h3>
          <button type="button" class="look-modal__close" aria-label="${this.closeText}">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M12 4L4 12M4 4l8 8" stroke="currentColor" stroke-width="1.5"/>
            </svg>
          </button>
        </div>
        <div class="look-modal__products">
          ${this.products.map((product, i) => this.renderProduct(product, i)).join('')}
        </div>
        <div class="look-modal__error" hidden></div>
        <div class="look-modal__footer">
          <button type="button" class="look-modal__submit" disabled>
            ${this.addText}
          </button>
        </div>
      </div>
    `;

    // Event listeners
    this.dialog.querySelector('.look-modal__close').addEventListener('click', () => this.close());
    this.dialog.addEventListener('click', (e) => {
      if (e.target === this.dialog) this.close();
    });
    this.dialog.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') this.close();
    });

    // Size pill clicks
    this.dialog.querySelectorAll('.look-modal__size-pill').forEach((pill) => {
      pill.addEventListener('click', () => this.selectSize(pill));
    });

    // Submit
    this.dialog.querySelector('.look-modal__submit').addEventListener('click', () => this.addToCart());

    this.updateSubmitState();
    this.appendChild(this.dialog);
  }

  renderProduct(product, index) {
    const availableVariants = product.variants.filter((v) => v.available);
    const selectedVariantId = this.selectedVariants[product.id];
    const hasOnlyDefault = product.variants.length === 1 && product.variants[0].title === 'Default Title';
    const isSoldOut = availableVariants.length === 0;

    return `
      <div class="look-modal__product" data-product-id="${product.id}">
        <div class="look-modal__product-image">
          <img src="${product.image}" alt="${product.title}" loading="lazy" />
        </div>
        <div class="look-modal__product-info">
          <span class="look-modal__product-title">${product.title}</span>
          <span class="look-modal__product-price">${product.price}</span>
          ${
            isSoldOut
              ? `<span class="look-modal__sold-out-note">${this.soldOutText}</span>`
              : hasOnlyDefault
              ? ''
              : `<div class="look-modal__sizes">
                  ${product.variants
                    .map(
                      (v) => `
                    <button
                      type="button"
                      class="look-modal__size-pill${v.available ? '' : ' look-modal__size-pill--disabled'}${
                        selectedVariantId === v.id ? ' look-modal__size-pill--selected' : ''
                      }"
                      data-product-id="${product.id}"
                      data-variant-id="${v.id}"
                      ${v.available ? '' : 'disabled'}
                      aria-pressed="${selectedVariantId === v.id ? 'true' : 'false'}"
                      aria-label="${v.title}${v.available ? '' : ` (${this.soldOutText})`}"
                    >${v.title}</button>
                  `
                    )
                    .join('')}
                </div>`
          }
        </div>
      </div>
    `;
  }

  selectSize(pill) {
    const productId = pill.dataset.productId;
    const variantId = parseInt(pill.dataset.variantId, 10);

    // Deselect siblings
    const container = pill.closest('.look-modal__product');
    container.querySelectorAll('.look-modal__size-pill').forEach((p) => {
      p.classList.remove('look-modal__size-pill--selected');
      p.setAttribute('aria-pressed', 'false');
    });

    // Select this one
    pill.classList.add('look-modal__size-pill--selected');
    pill.setAttribute('aria-pressed', 'true');
    this.selectedVariants[productId] = variantId;

    this.updateSubmitState();
  }

  updateSubmitState() {
    const submit = this.dialog.querySelector('.look-modal__submit');
    const errorEl = this.dialog.querySelector('.look-modal__error');
    const selectableProducts = this.getSelectableProducts();
    const allSelected = selectableProducts.every(
      (p) => this.selectedVariants[p.id] !== undefined
    );
    submit.disabled = !allSelected || selectableProducts.length === 0;
    errorEl.hidden = true;
  }

  async addToCart() {
    const submit = this.dialog.querySelector('.look-modal__submit');
    const errorEl = this.dialog.querySelector('.look-modal__error');

    // Validate all sizes selected
    const selectableProducts = this.getSelectableProducts();
    const allSelected = selectableProducts.every(
      (p) => this.selectedVariants[p.id] !== undefined
    );
    if (!allSelected) {
      errorEl.textContent = this.sizeRequiredText;
      errorEl.hidden = false;
      return;
    }

    submit.disabled = true;
    submit.textContent = '...';

    const items = selectableProducts
      .filter((p) => this.selectedVariants[p.id])
      .map((p) => ({
        id: this.selectedVariants[p.id],
        quantity: 1,
      }));

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((component) => component.dataset.sectionId)
      .filter(Boolean);

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
        }),
      });

      const data = await response.json();

      if (!response.ok || data.status) {
        throw new Error(data.message || `Cart error: ${response.status}`);
      }

      submit.textContent = this.addedText;
      setTimeout(() => {
        this.close();
        submit.textContent = this.addText;
        this.updateSubmitState();
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: data,
              sourceId: this.id || 'look-cart-modal',
              data: {
                source: 'product-form-component',
                itemCount: items.reduce((count, item) => count + item.quantity, 0),
                sections: data.sections,
              },
            },
          })
        );
      }, 1200);
    } catch (err) {
      errorEl.textContent = err.message;
      errorEl.hidden = false;
      submit.textContent = this.addText;
      submit.disabled = false;
    }
  }
}

if (!customElements.get('look-cart-modal')) {
  customElements.define('look-cart-modal', LookCartModal);
}
